import fetch, { fetchPeriodically } from 'logic/rest/FetchProvider';
import { qualifyUrl } from 'util/URLUtils';
import EnterpriseApiRoutes from 'common/ApiRoutes';
import type { SearchParams, Sort } from 'stores/PaginationTypes';
import { deserializePacks, buildIlluminateQuery } from 'illuminate/components/content-hub/utils';
import type {
  PacksIndexAPIType,
  Pack,
  PackFiltersType,
  PacksSummary,
  IlluminateHubFilterData,
  PacksSearchParams,
} from 'illuminate/types';
import type { EntitySharePayload } from 'actions/permissions/EntityShareActions';

export async function fetchHubLatestPacks(searchParams: PacksSearchParams) {
  const params = [
    `page=${searchParams.page}`,
    `per_page=${searchParams.pageSize}`,
    'include_deps=false',
    `sort=${searchParams.sort.attributeId}`,
    `direction=${searchParams.sort.direction}`,
  ];
  if (searchParams.query) params.push(`query=${encodeURI(searchParams.query)}`);

  const filters = searchParams.filters.toJS() ?? {};
  if (filters && Object.keys(filters).length > 0) params.push(`${encodeURI(buildIlluminateQuery(filters))}`);

  return Promise.all([
    fetch('PUT', qualifyUrl(EnterpriseApiRoutes.Illuminate.bundles.filterOptions().url), {
      fields: ['tags', 'category', 'input_type'],
    }),
    fetch('GET', qualifyUrl(EnterpriseApiRoutes.Illuminate.bundles.latest.searchPacksPaginated(params.join('&')).url)),
  ]).then(deserializePacks);
}

export const packEntitiesKeyFn = (searchParams: SearchParams) => ['packs', 'search', searchParams];

export const fetchLatestPacks = async (
  page: number,
  perPage: number,
  query: string = null,
  filters: IlluminateHubFilterData = {},
  includeDeps: boolean = false,
  sort: Sort = { attributeId: undefined, direction: undefined },
): Promise<PacksIndexAPIType> => {
  const params = [
    `page=${page}`,
    `per_page=${perPage}`,
    `include_deps=${includeDeps}`,
    `sort=${sort.attributeId}`,
    `direction=${sort.direction}`,
  ];
  if (query) params.push(`query=${encodeURI(query)}`);

  if (filters && Object.keys(filters).length > 0) {
    params.push(`${encodeURI(buildIlluminateQuery(filters))}`);
  }

  return fetch(
    'GET',
    qualifyUrl(EnterpriseApiRoutes.Illuminate.bundles.latest.searchPacksPaginated(params.join('&')).url),
  );
};

export const fetchThreatWidgetLatestPack = async ({
  page,
  includeDeps,
  query,
  filters,
  sort,
  pageSize,
}: PacksSearchParams) => {
  const response = await fetchLatestPacks(page, pageSize, query, filters.toJS(), includeDeps, sort);

  return deserializePacks([{ tags: [], category: [], input_types: [] }, response]);
};

export async function fetchAllLatestBundlePacks(): Promise<Array<Pack>> {
  return fetch('GET', qualifyUrl(EnterpriseApiRoutes.Illuminate.bundles.latest.searchPacks().url));
}

export const fetchPacksNoPaginated = async (filters: PackFiltersType): Promise<Pack[]> => {
  const params = Object.keys(filters).map(
    (key: string) => `${key}=${Array.isArray(filters[key]) ? filters[key].join(',') : filters[key]}`,
  );

  return fetch('GET', qualifyUrl(EnterpriseApiRoutes.Illuminate.bundles.latest.searchPacks(params.join('&')).url));
};

export const fetchPacksSummary = async (): Promise<PacksSummary> =>
  fetchPeriodically('GET', qualifyUrl(EnterpriseApiRoutes.Illuminate.bundles.latest.summary().url));

type EnableLatestUpdatePacks = {
  requestObject: {
    entity: {
      processing_pack_ids: string[];
      spotlight_pack_ids: string[];
    };
    share_request?: EntitySharePayload | undefined;
  };
};

type DisableLatestUpdatePacks = {
  requestObject: {
    processing_pack_ids: string[];
    spotlight_pack_ids: string[];
  };
};

export const latestEnablePacks = async ({ requestObject }: EnableLatestUpdatePacks): Promise<Pack[]> => {
  const { url } = EnterpriseApiRoutes.Illuminate.bundles.latest.enablePacks();

  return fetch('POST', qualifyUrl(url), requestObject);
};

export const latestDisablePacks = async ({ requestObject }: DisableLatestUpdatePacks): Promise<Pack[]> => {
  const { url } = EnterpriseApiRoutes.Illuminate.bundles.latest.disablePacks();

  return fetch('POST', qualifyUrl(url), requestObject);
};
